import React, { useMemo } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { tok, LABEL, MONO, panelSx, titleSx, fmtPct, fmtMoney, fmtDate } from './ui';

// The run's trades, newest first, with the API's net P&L and exit reason (DWLF-306). No client-side
// R multiple: per-trade performance figures come from the API or not at all (charter invariant 8).
const REASON = {
  stop_loss: { label: 'Stop', tone: 'down' }, take_profit: { label: 'Target', tone: 'up' },
  // A chandelier stop that had trailed (DWLF-341): often a profit, so not 'down'.
  trailing_stop: { label: 'Trailing stop', tone: 'ink-70' },
  signal_exit: { label: 'Signal exit', tone: 'ink-70' }, backtest_end: { label: 'Still open at end', tone: 'ink-55' },
};
const reasonOf = (r) => REASON[r] || { label: r ? String(r).replace(/_/g, ' ') : '—', tone: 'ink-55' };
const pnlOf = (t) => t.netPnL ?? t.pnl ?? null;
const pctOf = (t) => t.netPnLPercent ?? t.returnPercent ?? null;

export default function TradesPanel({ trades, onTradeClick, onExport }) {
  const rows = useMemo(() => [...(trades || [])].sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate)), [trades]);
  return (
    <Box sx={{ ...panelSx, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography component="h2" sx={titleSx}>Trades · {rows.length}</Typography>
        {onExport && (
          <ButtonBase onClick={onExport} sx={{ ...LABEL, fontSize: '0.625rem', color: tok('accent'), height: 32 }}>Export CSV</ButtonBase>
        )}
      </Box>
      {rows.length === 0 && <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-55') }}>No trades in this run.</Typography>}
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', maxHeight: 520, overflowY: 'auto' }}>
        {rows.map((t, i) => {
          const pnl = pnlOf(t);
          const pct = pctOf(t);
          const reason = reasonOf(t.exitReason);
          return (
            <Box component="li" key={`${t.symbol}-${t.entryDate}-${i}`}>
              <ButtonBase onClick={() => onTradeClick?.(t)} aria-label={`Open ${t.symbol} trade from ${fmtDate(t.entryDate)} on the chart`}
                sx={{ width: '100%', display: 'grid', gridTemplateColumns: '78px minmax(0, 1fr) 84px 92px', alignItems: 'center', gap: 1, minHeight: 44, px: 0.5,
                  borderBottom: `1px solid ${tok('ink-06')}`, textAlign: 'left', '&:hover': { backgroundColor: tok('ink-04') } }}>
                <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok('ink-55') }}>{fmtDate(t.entryDate)}</Typography>
                <Typography component="span" noWrap sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('text') }}>
                  {t.symbol} <Box component="span" sx={{ color: tok('ink-50') }}>{t.direction || ''}</Box>
                </Typography>
                <Typography component="span" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok(reason.tone) }}>{reason.label}</Typography>
                <Box component="span" sx={{ textAlign: 'right', fontFamily: MONO, fontSize: '0.8125rem', color: tok(pnl === null ? 'ink-55' : pnl >= 0 ? 'up' : 'down') }}>
                  {fmtPct(pct, 1)}
                  <Box component="span" sx={{ display: 'block', fontSize: '0.6875rem', color: tok('ink-50') }}>{fmtMoney(pnl)}</Box>
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
